import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Leaf, Mail, ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-700 text-white mb-3">
            <Leaf className="w-6 h-6" />
          </span>
          <h1 className="font-display text-3xl text-emerald-950 font-light tracking-tight">
            Forgot your password?
          </h1>
          <p className="text-emerald-900/60 mt-2 text-sm">
            Enter your email and we'll send you a secure link to choose a new one.
          </p>
        </div>

        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            {submitted ? (
              <div data-testid="forgot-success" className="text-center space-y-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-700">
                  <Mail className="w-6 h-6" />
                </span>
                <h2 className="font-display text-xl text-emerald-950">Check your inbox</h2>
                <p className="text-sm text-emerald-900/70 leading-relaxed">
                  If an account exists for <strong>{email}</strong>, we've just sent a password
                  reset link. The link expires in 60 minutes.
                </p>
                <p className="text-xs text-emerald-900/60">
                  Didn't get it? Check your spam folder, or{" "}
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="text-emerald-700 font-semibold underline"
                    data-testid="forgot-try-again"
                  >
                    try a different email
                  </button>
                  .
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm hover:underline"
                  data-testid="forgot-back-login"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-emerald-900">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                {error ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="forgot-error">
                    {error}
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
                <p className="text-center text-sm text-emerald-900/70">
                  Remembered it?{" "}
                  <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="forgot-to-login">
                    Back to login
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
